import { Checkbox, Field, NumericInput, RadioGroup, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from 'Components/Layout';
import {
  IWorksheetComponent,
  WorksheetAssociatedTestSection,
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
} from 'Components/Worksheets/Core';
import {
  OversizeMaterial,
  SampleSusceptibleToCrushing,
} from 'Components/Worksheets/MoistureConditionValueWorksheet/types';
import { UsedSubSamples } from 'Components/Worksheets/Shared/types';
import { hasValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import { useSelector } from 'react-redux';
import TestDefinitionGridSection from './TestDefinitionGridSection';

let PreparationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const sampleSusceptibleToCrushingOptions: Array<{ label: string; value: SampleSusceptibleToCrushing }> = [
    { label: 'Susceptible', value: SampleSusceptibleToCrushing.Susceptible },
    { label: 'Not Susceptible', value: SampleSusceptibleToCrushing.NotSusceptible },
  ];

  const usedSubSamplesOptions: Array<{ label: string; value: UsedSubSamples }> = [
    { label: 'Single sub-sample', value: UsedSubSamples.Single },
    { label: 'Multiple sub-samples', value: UsedSubSamples.Multiple },
  ];

  const oversizeMaterialOptions: Array<{ label: string; value: OversizeMaterial }> = [
    { label: 'Measured', value: OversizeMaterial.Measured },
    { label: 'No Oversize Material', value: OversizeMaterial.NoOversize },
    { label: 'Taken from another test (direct entry)', value: OversizeMaterial.TakenFromAnotherTest },
  ];

  const sampleSuscebtibleToCrushing = useSelector(
    pipe(createWorksheetSelector!('Preparation.SampleSusceptibleToCrushing'), (o: SampleSusceptibleToCrushing) => {
      return o === SampleSusceptibleToCrushing.Susceptible;
    })
  );

  const oversizeMaterial = useSelector(
    createWorksheetSelector!('Preparation.OversizeMaterial'),
    deepEquals
  ) as OversizeMaterial;

  const hasMassRetainedOn20_0mmSieveValue = useSelector(
    pipe(createWorksheetSelector!('Preparation.MassRetainedOn20_0mmSieve'), hasValue)
  );

  const hasMassRetainedOn20_0mmSievePercentage = useSelector(
    pipe(createWorksheetSelector!('Preparation.MassRetainedOn20_0mmSievePercentage'), hasValue)
  );

  const isMassRetainedOn20_0mmSievePercentageGreaterThanZero = useSelector(
    pipe(createWorksheetSelector!('Preparation.MassRetainedOn20_0mmSievePercentage'), (t) => t > 0)
  );

  const isOversizeMeasuredAndValid =
    oversizeMaterial === OversizeMaterial.Measured &&
    (!hasMassRetainedOn20_0mmSievePercentage ||
      (hasMassRetainedOn20_0mmSievePercentage && isMassRetainedOn20_0mmSievePercentageGreaterThanZero));

  return (
    <>
      <ListFieldSection>
        <Field
          input={<Checkbox />}
          name="Preparation.HandShearVaneResultRequired"
          confirm={({ value }) => value !== true}
          confirmTitle="Unchecking Hand Shear Vane Result Required"
          confirmText="Unchecking this option will delete all hand shear vane tests. Are you sure you want to continue?"
          confirmButtonText="Confirm"
        />
        <Field
          input={<RadioGroup />}
          name="Preparation.SampleSusceptibleToCrushing"
          options={sampleSusceptibleToCrushingOptions}
        />
        <Field
          input={<RadioGroup />}
          name="Preparation.UsedSubSamples"
          options={usedSubSamplesOptions}
          visible={!sampleSuscebtibleToCrushing}
        />
        <Field
          input={<TextOutput />}
          name="Preparation.UsedSubSamples"
          value={usedSubSamplesOptions.find((x) => x.value === UsedSubSamples.Multiple)?.label}
          visible={sampleSuscebtibleToCrushing}
        />
        <WorksheetBalanceFieldSet name="Preparation.Balance" />
        <Field
          input={<RadioGroup />}
          name="Preparation.OversizeMaterial"
          options={oversizeMaterialOptions}
          confirm={oversizeMaterial === OversizeMaterial.Measured && hasMassRetainedOn20_0mmSieveValue}
          confirmTitle="Confirm Oversize Material change"
          confirmText="This will clear the mass retained on the 20mm sieve and cannot be undone, continue?"
          confirmButtonText="Confirm"
        />
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="sieve"
          name="Preparation.Sieve"
          queryParams={{ SieveSize: '0.020 m' }}
          visible={oversizeMaterial === OversizeMaterial.Measured}
        />
        <Field
          input={<NumericInput />}
          name="Preparation.TareMass"
          visible={oversizeMaterial === OversizeMaterial.Measured}
        />
        <Field
          input={<NumericInput />}
          name="Preparation.TareAndTotalSampleMass"
          visible={oversizeMaterial === OversizeMaterial.Measured}
        />
        <Field
          input={<TextOutput />}
          name="Preparation.TotalSampleMass"
          visible={oversizeMaterial === OversizeMaterial.Measured}
        />
        <Field
          input={<NumericInput />}
          name="Preparation.MassRetainedOn20_0mmSieve"
          visible={oversizeMaterial === OversizeMaterial.Measured}
        />
        <FieldSet visible={oversizeMaterial !== OversizeMaterial.TakenFromAnotherTest}>
          <Field input={<TextOutput />} name="Preparation.MassRetainedOn20_0mmSievePercentage" />
          <Field
            visible={oversizeMaterial === OversizeMaterial.NoOversize}
            input={<TextOutput />}
            name="Preparation.DryMassRetainedOn20_0mmSievePercentage"
          />
        </FieldSet>
        <Field
          input={<NumericInput />}
          name="Preparation.DryMassRetainedOn20_0mmSievePercentage"
          visible={oversizeMaterial === OversizeMaterial.TakenFromAnotherTest}
        />
        <Field
          input={<TextOutput />}
          name="Preparation.DryMassRetainedOn20_0mmSievePercentage"
          visible={oversizeMaterial === OversizeMaterial.Measured && !isOversizeMeasuredAndValid}
        />
      </ListFieldSection>
      <ListFieldSection heading="Passing 20mm As-Received Water Content" wide visible={isOversizeMeasuredAndValid}>
        <WorksheetAssociatedTestSection
          name="Preparation.EmbeddedWaterContentTest"
          testType="Moisture Content"
          heading="Water Content BS EN ISO 17892-1"
          associatedTestModel={{
            measured: true,
          }}
        />
      </ListFieldSection>
      <ListFieldSection>
        <Field
          input={<TextOutput />}
          name="Preparation.TotalSampleDryMass"
          visible={oversizeMaterial === OversizeMaterial.Measured && isOversizeMeasuredAndValid}
        />
        <Field
          input={<TextOutput />}
          name="Preparation.DryMassRetainedOn20_0mmSievePercentage"
          visible={oversizeMaterial !== OversizeMaterial.TakenFromAnotherTest && isOversizeMeasuredAndValid}
        />
      </ListFieldSection>
      <TestDefinitionGridSection />
    </>
  );
};

PreparationSection = withInjectedProps(PreparationSection);

export { PreparationSection };
export default PreparationSection;
